/**
 * CalendarTimezoneBanner Component
 * Dismissible info banner shown when campaign timezone differs from browser timezone.
 * Dismissal is persisted in localStorage per campaign + timezone.
 */

import { useState, useEffect } from 'react';
import { Info, X } from 'lucide-react';
import { useCampaignStore } from '@/stores/campaignStore';
import { getTimezoneAbbreviation } from '@/lib/calendarUtils';
import { cn } from '@/lib/utils';

const STORAGE_PREFIX = 'tz-banner-dismissed:';

/** Get friendly timezone label from IANA id */
function getTimezoneFriendlyName(timeZoneId: string): string {
  const map: Record<string, string> = {
    'America/New_York': 'Eastern Time',
    'America/Chicago': 'Central Time',
    'America/Denver': 'Mountain Time',
    'America/Los_Angeles': 'Pacific Time',
    'America/Anchorage': 'Alaska Time',
    'Pacific/Honolulu': 'Hawaii Time',
    'America/Phoenix': 'Arizona Time',
    'America/Indiana/Indianapolis': 'Indiana Eastern Time',
  };
  return map[timeZoneId] || getTimezoneAbbreviation(timeZoneId) || timeZoneId;
}

export function CalendarTimezoneBanner() {
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);
  const timeZoneId = selectedCampaign?.timeZoneId;
  const [dismissed, setDismissed] = useState(true); // Default to hidden until we check

  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isDifferent = !!timeZoneId && localTz !== timeZoneId;

  // Check localStorage on mount / when campaign or timezone changes
  useEffect(() => {
    if (!isDifferent || !selectedCampaign) {
      setDismissed(true);
      return;
    }
    const key = `${STORAGE_PREFIX}${selectedCampaign.id}:${timeZoneId}`;
    setDismissed(localStorage.getItem(key) === 'true');
  }, [isDifferent, selectedCampaign?.id, timeZoneId, selectedCampaign]);

  if (!isDifferent || dismissed || !timeZoneId) return null;

  const handleDismiss = () => {
    if (selectedCampaign) {
      const key = `${STORAGE_PREFIX}${selectedCampaign.id}:${timeZoneId}`;
      localStorage.setItem(key, 'true');
    }
    setDismissed(true);
  };

  const friendlyName = getTimezoneFriendlyName(timeZoneId);

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 px-4 py-2',
        'bg-blue-50 border-b border-blue-200 text-blue-800 text-sm'
      )}
    >
      <div className="flex items-center gap-2">
        <Info className="h-4 w-4 shrink-0" />
        <span>
          Calendar times are shown in <strong>{friendlyName}</strong> (campaign timezone)
        </span>
      </div>
      <button
        onClick={handleDismiss}
        className="shrink-0 p-0.5 rounded hover:bg-blue-100 transition-colors"
        aria-label="Dismiss timezone notice"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export default CalendarTimezoneBanner;
